import { Head, Link, router, useForm } from '@inertiajs/react'
import { AlertTriangle, ArrowLeft, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { editCompanyPath } from '@/lib/routes'
import { getRememberedUrl } from '@/lib/rememberedUrl'
import { foundersPath, founderPath } from '@/lib/routes'

import {
  FounderFormFields,
  type CountryOption,
  type FounderFormData,
} from './_Form'

type Affiliation = {
  id: number
  company_id: number
  company_name: string
  company_slug: string | null
  role: string | null
  joined_at: string | null
  left_at: string | null
}

type EditFounder = Omit<FounderFormData, 'avatar' | 'remove_avatar'> & {
  id: number
  slug: string
  avatar_preview_url: string | null
  avatar_attached: boolean
  affiliations: Affiliation[]
}

type FoundersEditProps = {
  founder: EditFounder
  countries: CountryOption[]
}

function FoundersEdit({ founder, countries }: FoundersEditProps) {
  const form = useForm<FounderFormData>({
    name: founder.name ?? '',
    email: founder.email ?? '',
    gender: founder.gender ?? '',
    linkedin_url: founder.linkedin_url ?? '',
    twitter_url: founder.twitter_url ?? '',
    github_url: founder.github_url ?? '',
    website_url: founder.website_url ?? '',
    date_of_birth: founder.date_of_birth ?? '',
    country: founder.country ?? '',
    kalio_id: founder.kalio_id ?? null,
    avatar: null,
    remove_avatar: false,
  })

  const backToIndex = getRememberedUrl('founders', foundersPath())

  return (
    <>
      <Head title={`${founder.name} — Founders`} />

      <div className="mx-auto max-w-3xl px-8 py-10">
        <Link
          href={backToIndex}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to founders
        </Link>

        <PageHeader title={founder.name} />

        <form
          onSubmit={(event) => {
            event.preventDefault()
            form.transform((data) => ({ founder: data }))
            form.patch(founderPath(founder.slug), { forceFormData: true })
          }}
        >
          <FounderFormFields
            data={form.data}
            errors={form.errors as Partial<Record<keyof FounderFormData, string>>}
            setData={form.setData}
            countries={countries}
            existingAvatarUrl={founder.avatar_preview_url ?? null}
            avatarIsUpload={founder.avatar_attached}
          />

          <div className="mt-6 flex justify-end gap-2">
            <Link href={backToIndex}>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </Link>
            <Button type="submit" disabled={form.processing}>
              {form.processing ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>

        {founder.affiliations.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Companies</CardTitle>
              <CardDescription>
                Every tenure window — role changes and re-joins each get their own row.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="px-6 py-2 text-left font-medium">Company</th>
                    <th className="px-6 py-2 text-left font-medium">Role</th>
                    <th className="px-6 py-2 text-left font-medium">From</th>
                    <th className="px-6 py-2 text-left font-medium">To</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {founder.affiliations.map((a) => (
                    <tr key={a.id}>
                      <td className="px-6 py-3">
                        {a.company_slug ? (
                          <Link
                            href={editCompanyPath(a.company_slug)}
                            className="underline-offset-4 hover:underline"
                          >
                            {a.company_name}
                          </Link>
                        ) : (
                          a.company_name
                        )}
                      </td>
                      <td className="px-6 py-3 text-muted-foreground">{a.role ?? '—'}</td>
                      <td className="px-6 py-3 text-muted-foreground">{a.joined_at ?? '—'}</td>
                      <td className="px-6 py-3 text-muted-foreground">
                        {a.left_at ?? (
                          <span className="text-emerald-600 dark:text-emerald-400">active</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        <Card className="mt-6 border-destructive/40">
          <CardHeader>
            <CardTitle className="text-destructive">Archive founder</CardTitle>
            <CardDescription>
              The founder is soft-deleted and hidden from lists. Company affiliations stay
              intact for historical integrity.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={() => {
                if (window.confirm(`Archive ${founder.name}?`)) {
                  router.delete(founderPath(founder.slug))
                }
              }}
            >
              <AlertTriangle />
              <Trash2 />
              Archive
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

export default Object.assign(FoundersEdit, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
